import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import "@/styles/badges.css";

/**
 * Renders a single badge tile. Click → optional onClick handler. Hover
 * shows the badge name as a tooltip.
 */
export function BadgeTile({ badge, size = 24, locked = false, onClick = null, showTooltip = true }) {
  if (!badge) return null;
  const cls = `badge-frame rarity-${badge.rarity || "common"} ${locked ? "is-locked" : ""}`;
  const inner = badge.image_url
    ? <img src={badge.image_url} alt={badge.name} style={{ maxWidth: size - 6, maxHeight: size - 6 }} />
    : <span style={{ fontSize: size * 0.6, lineHeight: 1 }}>{badge.icon_emoji || "•"}</span>;
  const style = { width: size, height: size, fontSize: size * 0.6 };
  const Inner = (
    <span className={cls} style={style} data-testid={`badge-tile-${badge.sku}`}>
      {inner}
      {showTooltip && (
        <span className="badge-tooltip">{badge.name}</span>
      )}
    </span>
  );
  if (onClick) {
    return <button type="button" onClick={onClick} className="p-0 bg-transparent border-0">{Inner}</button>;
  }
  return Inner;
}

/**
 * Inline strip of equipped badges next to a username. Shows up to `max`
 * (default 5); excess collapse into a "+N" chip linking to the user's
 * profile / badges page.
 */
export function BadgeStrip({ username, badges = null, max = 5, size = 22 }) {
  // Lazy-load if only username given (no badges array provided).
  const [resolved, setResolved] = useState(badges);
  useEffect(() => {
    if (badges !== null) { setResolved(badges); return; }
    if (!username) return;
    let cancelled = false;
    api.get(`/badges/by-username/${encodeURIComponent(username)}`)
      .then(({ data }) => { if (!cancelled) setResolved(data?.equipped || []); })
      .catch(() => { if (!cancelled) setResolved([]); });
    return () => { cancelled = true; };
  }, [username, badges]);

  if (!resolved || resolved.length === 0) return null;
  const shown = resolved.slice(0, max);
  const overflow = Math.max(0, resolved.length - max);
  return (
    <span className="inline-flex items-center gap-1 align-middle" data-testid={`badge-strip-${username || "anon"}`}>
      {shown.map((b) => (
        <BadgeTile key={b.sku} badge={b} size={size} />
      ))}
      {overflow > 0 && username && (
        <Link
          to={`/u/${encodeURIComponent(username)}/badges`}
          className="badge-overflow"
          style={{ height: size, minWidth: size }}
          data-testid={`badge-overflow-${username}`}
          title={`${overflow} more`}
        >
          +{overflow}
        </Link>
      )}
      {overflow > 0 && !username && (
        <span className="badge-overflow" style={{ height: size }}>+{overflow}</span>
      )}
    </span>
  );
}

export default BadgeStrip;
